/**
 * Minimal gradient sampling for the home swarm. Colors are returned in
 * LINEAR rgb (three's working space) so they feed the point shader directly,
 * matching the sim's pipeline. Pure module, no three dependency.
 */

export type GradientStop = { t: number; color: string };

/** Neon orange / fire, hot at t=0 (the wordmark center) out to deep red. */
export const FIRE_RADIAL: GradientStop[] = [
  { t: 0, color: "#fff3c4" },
  { t: 0.35, color: "#ffb020" },
  { t: 0.7, color: "#ff5a00" },
  { t: 1, color: "#d61e00" },
];

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** "#rrggbb" to linear rgb 0..1. */
export function hexToLinear(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [
    srgbToLinear(((v >> 16) & 255) / 255),
    srgbToLinear(((v >> 8) & 255) / 255),
    srgbToLinear((v & 255) / 255),
  ];
}

/** Sample a gradient at t in 0..1. Pure. */
export function sampleGradient(stops: GradientStop[], t: number): [number, number, number] {
  const s = stops;
  if (t <= s[0].t) return hexToLinear(s[0].color);
  const last = s[s.length - 1];
  if (t >= last.t) return hexToLinear(last.color);
  for (let i = 0; i < s.length - 1; i++) {
    if (t >= s[i].t && t <= s[i + 1].t) {
      const span = s[i + 1].t - s[i].t || 1;
      const f = (t - s[i].t) / span;
      const a = hexToLinear(s[i].color);
      const b = hexToLinear(s[i + 1].color);
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, a[2] + (b[2] - a[2]) * f];
    }
  }
  return hexToLinear(last.color);
}

/**
 * Color flat (xy) points by radius from their bounding-box center:
 * t=0 at the center, t=1 at the farthest point. Free's fire radial.
 */
export function radialColors(positions: Float32Array, stops: GradientStop[]): Float32Array {
  const n = positions.length / 3;
  const out = new Float32Array(positions.length);
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (let i = 0; i < n; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1];
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  const cx = (minx + maxx) / 2, cy = (miny + maxy) / 2;
  let maxr = 1e-6;
  const rr = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const r = Math.hypot(positions[i * 3] - cx, positions[i * 3 + 1] - cy);
    rr[i] = r;
    if (r > maxr) maxr = r;
  }
  for (let i = 0; i < n; i++) {
    const [r, g, b] = sampleGradient(stops, rr[i] / maxr);
    out[i * 3] = r;
    out[i * 3 + 1] = g;
    out[i * 3 + 2] = b;
  }
  return out;
}
